import { UtilityService } from '../utility.service';
import { ApiService } from '../api-service';
import { DataService } from '../utilities/data.service';
import { Consent } from '../../dataTypes/type-consent';
import { GetConsentsService } from './get-consents.service';
import { ConsentsStatusService } from './consents-status.service';
import { LanguageCode } from '../../Enums/LanguageCode.enums';
import { UcErrorMessage } from '../../Enums/uc-errors.enum';
const _ = require('lodash');
const uuid = require('uuidv4');
const SHA256 = require('sha256');

export class InitializeConsentsService {
    private apiService: ApiService;
    private consentTemplateAccessCount: number = 0;
    private isInitialPageLoad: boolean = false;
    private MAX_ALLOWED_TEMPLATE_FETCH_ATTEMPT: number = 3;
    private utilityService: UtilityService;

    constructor(utilityService: UtilityService) {
        this.apiService = utilityService.apiService;
        this.utilityService = utilityService;
        InitializeConsentsService.initConsentsFromLocalStorage();
    }

    static initConsentsFromLocalStorage() {
        const usercentrics = DataService.getConsentsFromLocalStorage();
        if (usercentrics) {
            InitializeConsentsService.fetchConsentsFromLocalStorage(usercentrics);
            return;
        }

        const deprecatedUsercentrics = DataService.getLocalStorage();
        if(deprecatedUsercentrics) {
            InitializeConsentsService.fetchConsentsFromLocalStorage(deprecatedUsercentrics);
        } else {
            DataService.storeGlobally('controllerId', InitializeConsentsService.generateIdentityId());
        }
    }

    static fetchConsentsFromLocalStorage(usercentrics) {
        if (usercentrics.consents && usercentrics.consents.length) {
            if (usercentrics.consents[0].hasOwnProperty('unsavedConsentStatus')) {
                DataService.storeGlobally('consents', usercentrics.consents);
            } else {
                const consents = InitializeConsentsService.mapOldConsent(usercentrics.consents);
                DataService.storeGlobally('consents', consents);
            }
        }

        if (usercentrics.categories && usercentrics.categories.length && !usercentrics.categories[0].hasOwnProperty('id')) {
            if (usercentrics.consents) {
                const categories = InitializeConsentsService.updateConsentsInCategory(usercentrics.categories, DataService.consents);
                DataService.storeGlobally('categories', categories);
            }
        }

        if (usercentrics.consentTemplates) {
            DataService.storeGlobally('consentTemplates', usercentrics.consentTemplates);
        }

        if (usercentrics.controllerId) {
            DataService.storeGlobally('controllerId', usercentrics.controllerId);
        } else {
            DataService.storeGlobally('controllerId', InitializeConsentsService.generateIdentityId());
        }
    }

    async initCategoriesAndConsents(settings, isNeedToReset) {
        await this.initConsentTemplates(settings.consentTemplates);
        this.initCategories(settings.categories);
        const isUpdateConsentStatus = isNeedToReset || !DataService.consents;
        this.initConsents(settings.consentTemplates, isUpdateConsentStatus);
        const categories = InitializeConsentsService.updateConsentsInCategory(DataService.categories ,DataService.consents);
        DataService.storeGlobally('categories', categories);

        if (this.isInitialPageLoad) {
            this.saveInitialConsents();
        }

        ConsentsStatusService.setSelectedStateForCategories(DataService.categories);
        this.utilityService.setTypeOfScriptTagForConsents(DataService.consents);
        
        if (this.utilityService.crossDomainConsentSharingService.updateConsentsAfterGettingTemplates) {
            this.utilityService.crossDomainConsentSharingService.updateConsentsAfterGettingTemplates = false;
            this.utilityService.cDCSyncService.syncConsentDataWithCdcsIframeWhenConsentsAreNotReset();
        }
    }

    static getMappedCategories(categories): [Consent.Category] {
        return categories.map((category) => {
            if (!category.hasOwnProperty('categorySlug')) {
                throw 'categorySlug not provided in one of the category';
            }

            if (!category.hasOwnProperty('label')) {
                throw 'label not provided in one of the category';
            }

            const mappedCategory: Consent.Category = {
                categorySlug: category.categorySlug,
                description: category.hasOwnProperty('description') ? category.description : '',
                isEssential: category.hasOwnProperty('isEssential') ? category.isEssential : false,
                isHidden: category.hasOwnProperty('isHidden') ? category.isHidden : false,
                label: category.label,
                status: false,
                isIntermediateState: false,
                consents: []
            };
            return mappedCategory;
        });
    }

    initCategories(categories) {
        this.utilityService.developmentLog('initializing categories');
        const mappedCategories = InitializeConsentsService.getMappedCategories(categories);
        
        if (DataService.categories && DataService.categories.length) {
            const overwriteFields = ['status', 'isIntermediateState', 'showDetail'];

            mappedCategories.forEach(category => {
                const savedCategory = DataService.categories.find(c => c.categorySlug === category.categorySlug);

                if (savedCategory) {
                    overwriteFields.forEach(element => {
                        category[element] = savedCategory[element];
                    });
                }
            });
        }

        DataService.storeGlobally('categories', mappedCategories);
    }

    static generateIdentityId() {
        return SHA256(uuid());
    }

    static getMappedConsents(consents, areSubConsents?): Consent.Consent[] {
        const mappedConsents = [];

        consents.forEach(consent => {
            const defaultConsentStatus = consent.hasOwnProperty('defaultConsentStatus') && consent.defaultConsentStatus ? 
                true: false;
            const mappedConsent: Consent.Consent = {
                controllerId: undefined,
                processorId: undefined,
                templateId: consent.templateId,
                version: consent.version,
                categorySlug: consent.categorySlug,
                defaultConsentStatus: defaultConsentStatus,
                consentStatus: defaultConsentStatus,
                unsavedConsentStatus: defaultConsentStatus,
                description: consent.hasOwnProperty('description') ? consent.description : '',
                isDeactivated: consent.hasOwnProperty('isDeactivated') ? consent.isDeactivated : false,
                isHidden: consent.hasOwnProperty('isHidden') ? consent.isHidden : false,
                isShared: consent.hasOwnProperty('isShared') ? consent.isShared : false,
                action: 'onInitialPageLoad',
                updatedBy: 'implicit',
                history: [],
            };

            if (!areSubConsents) {
                // @dave check with backend if subconsents = [''] issue is solved or not?
                mappedConsent.subConsents = consent.subConsents && consent.subConsents.length && consent.subConsents[0] ?
                    InitializeConsentsService.getMappedConsents(consent.subConsents, true) : [];
            }

            if (!mappedConsent.isDeactivated) {
                mappedConsents.push(mappedConsent);
            }
        });

        return mappedConsents;
    }

    initConsents(consents, isUpdateConsentStatus) {
        this.utilityService.developmentLog('init consents with isUpdateConsentStatus = ', isUpdateConsentStatus);
        const mappedConsents = InitializeConsentsService.getMappedConsents(consents);

        if (DataService.consents && DataService.consents.length) {
            const overwriteFieldsIfUpdateNotNeeded = ['action', 'updatedBy', 'consentStatus', 'unsavedConsentStatus'];
            const overwriteFields = ['controllerId', 'processorId', 'history'];

            mappedConsents.forEach(consent => {
                const savedConsent = DataService.consents.find(c => c.templateId === consent.templateId);

                if (savedConsent) {
                    overwriteFields.forEach(element => {
                        consent[element] = savedConsent[element];
                    });

                    if (isUpdateConsentStatus) {
                        consent.action = 'bySettingsUpdate';
                        consent.updatedBy = 'update';
                    } else {
                        overwriteFieldsIfUpdateNotNeeded.forEach(element => {
                            consent[element] = savedConsent[element];
                        });
                    }
                }
            });

            DataService.storeGlobally('consents', mappedConsents);
        } else {
            mappedConsents.forEach(consent => {
                consent.controllerId = DataService.controllerId;
                consent.processorId = InitializeConsentsService.generateIdentityId();
            });

            DataService.storeGlobally('consents', mappedConsents);
            this.isInitialPageLoad = true;
        }
    }

    saveInitialConsents() {
        const action = 'onInitialPageLoad';
        const updatedBy = 'implicit';
        this.utilityService.saveConsentsService.saveConsents(action, updatedBy, true);
    }

    static generateHashArray(consents) {
        let hashArray = [];
        consents.forEach(consent => {
            hashArray.push({
                id: consent.templateId,
                version: consent.version,
                language: DataService.settings.language
            });

            if (consent.subConsents && consent.subConsents.length && consent.subConsents[0]) {
                hashArray = hashArray.concat(InitializeConsentsService.generateHashArray(consent.subConsents));
            }
        });
        return hashArray;
    }

    static generateHashOfConsents(hashArray) {
        const query = _.sortBy(hashArray, 'id');
        return SHA256(JSON.stringify(query));
    }

    static formatTemplates(templates: Consent.ConsentTemplate[]): Consent.ConsentTemplates {
        const propertiesToDelete = ['createdAt', 'updatedAt', 'languagesAvailable', 'isLatest', 'type', 'shareCustomConsent', 'index'];
        const propertiesSupposedToBeArrays = ['dataPurposesList', 'dataCollectedList', 'dataRecipientsList', 'technologyUsed', 'legalBasisList'];
        const propertiesToMove = [
            {from: 'processingCompany', to: 'nameOfProcessingCompany'},
            {from: 'policyOfProcessorUrl', to: 'privacyPolicyURL'},
            {from: 'dataPurposes', to: 'dataPurposesList'},
        ];
        const formattedTemplates: Consent.ConsentTemplates = {};

        templates.forEach(element => {
            propertiesToDelete.forEach(prop => {
                if(element.hasOwnProperty(prop)) {
                    delete element[prop];
                }
            });

            propertiesSupposedToBeArrays.forEach(prop => {
                if (element.hasOwnProperty(prop) && element[prop] && !Array.isArray(element[prop])) {
                    element[prop] = [element[prop]];
                }
            });

            propertiesToMove.forEach(field => {
                if(element[field.from] && (!element[field.to] || Array.isArray(element[field.to]) && element[field.to].length === 0))  {
                    element[field.to] = element[field.from];
                }
            });

            if (!formattedTemplates.hasOwnProperty(element.templateId)) {
                formattedTemplates[element.templateId] = {[element.version]: element};
            } else {
                formattedTemplates[element.templateId][element.version] = element;
            }
        });

        return formattedTemplates;
    }

    async initConsentTemplates(consents) {
        const hashArray = InitializeConsentsService.generateHashArray(consents);
        const consentsHash = InitializeConsentsService.generateHashOfConsents(hashArray);

        if (DataService.consentTemplates && DataService.consentsHash === consentsHash) {
            return;
        }

        this.consentTemplateAccessCount = 0;
        const templates = await this.getMultiConsentFromServer(hashArray, consentsHash);
        const formattedTemplates = InitializeConsentsService.formatTemplates(templates);
        DataService.storeGlobally('consentTemplates', formattedTemplates);
    }

    async getMultiConsentFromServer(hashArray, consentsHash) {
        if (this.consentTemplateAccessCount > this.MAX_ALLOWED_TEMPLATE_FETCH_ATTEMPT) {
            throw UcErrorMessage.MAX_ATTEMPT_EXCEEDED;
        }

        this.consentTemplateAccessCount++;
        try {
            return await this.apiService.getMultiConsentsData(consentsHash);
        } catch (error) {
            const response = await ApiService.getMultiConsentFromServer(hashArray);

            if (response && response.fileName) {
                return await this.getMultiConsentFromServer(hashArray, consentsHash);
            } else if (response.error) {
                const missingConsents = response.error.map(missingConsent => missingConsent.id);

                for (let i = 0; i < hashArray.length; i++) {
                    if (missingConsents.indexOf(hashArray[i].id) !== -1) {
                        hashArray[i].language = LanguageCode.default;
                    }
                }

                const updatedConsentsHash = InitializeConsentsService.generateHashOfConsents(hashArray);
                return await this.getMultiConsentFromServer(hashArray, updatedConsentsHash);
            }

            return undefined;
        }
    }

    static updateConsentsInCategory(categories, consents): Consent.Category[] {
        const localCategories = InitializeConsentsService.resetConsentsInCategory(categories);

        consents.forEach(consent => {
            if (consent.templateId !== GetConsentsService.optinTemplateId) {
                const category = GetConsentsService.getAppropriateCategory(localCategories, consent.categorySlug);

                if (category) {
                    category.consents.push(consent);

                    if (category.isEssential) {
                        ConsentsStatusService.setUnsavedConsentStatus(consent, true);
                    }
                } else {
                    categories[0].consents.push(consent);
                }
            }
        });

        const filteredCategories = localCategories.filter((category) => category.consents.length);
        return filteredCategories;
    }

    static resetConsentsInCategory(categories) {
        const localCategories = JSON.parse(JSON.stringify(categories));
        return localCategories.map((category) => {
            category.consents = [];
            return category;
        });
    }

    static mapOldHistory(consent): Consent.History[] {
        if (!consent.history) {
            return [];
        }

        return consent.history.map((hist) => {
            return {
                action: 'oldSettingAction',
                updatedBy: 'localStorage',
                appVersion: 'old',
                settingsVersion: 'old',
                consentTemplateVersion: consent.version,
                consentStatus: hist.consentStatus,
                language: 'old',
                updatedAt: new Date(hist.updatedAt).getTime(),
                templateId: consent.templateId
            };
        });
    }

    static mapOldConsent(consents, areSubConsents?): Consent.Consent[] {
        return consents.map((consent) => {
            const mappedConsent: Consent.Consent = {
                controllerId: consent.controllerId,
                processorId: consent.processorId,
                templateId: consent.templateId,
                version: consent.version,
                categorySlug: consent.categorySlug,
                defaultConsentStatus: consent.defaultConsentStatus,
                consentStatus: consent.consentStatus,
                unsavedConsentStatus: consent.consentStatus,
                description: consent.hasOwnProperty('description') ? consent.description : '',
                isDeactivated: consent.hasOwnProperty('isDeactivated') ? consent.isDeactivated : false,
                isHidden: consent.hasOwnProperty('isHidden') ? consent.isHidden : false,
                isShared: consent.hasOwnProperty('isShared') ? consent.isShared : false,
                action: 'oldSettingAction',
                updatedBy: 'localStorage',
                history: InitializeConsentsService.mapOldHistory(consent),
            };

            if (!areSubConsents) {
                mappedConsent.subConsents = consent.subConsents && consent.subConsents.length && consent.subConsents[0] && consent.subConsents[0] !== '' ?
                    InitializeConsentsService.mapOldConsent(consent.subConsents, true) : [];
            }

            return mappedConsent;
        });
    }
}
